/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Type } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { CoreMlsModule } from '@gh/core-mls';
import { CoreTypeModule } from '@gh/core-type';
import { CoreUiCommonModule, CoreUiDialogsModule, CoreUiFormModule } from '@gh/core-ui';
import { Grid } from 'ag-grid-community';

import {
	GridAutocompleteCellEditorComponent,
	GridButtonCellRendererComponent,
	GridCellEditorContainerComponent,
	GridCellTooltipDirective,
	GridCheckboxCellRendererComponent,
	GridEditableCellRendererComponent,
	GridMaskedCellEditorComponent,
	GridNumberCellEditorComponent,
	GridQuantityCellEditorComponent,
	GridSelectCellEditorComponent,
	GridTemplateCellRendererComponent,
	GridTextareaCellEditorComponent,
	GridTextCellEditorComponent,
	GridTooltipCellRendererComponent,
} from './cell-components';
import {
	GridDateTimeCellEditorComponent
} from './cell-components/editors/grid-input-cell-editor/grid-date-time-cell-editor.component';
import { GridRadioCellRendererComponent } from './cell-components/renderers';

import {
	GridCellTemplateDirective,
	GridColumnComponent,
	GridComponent,
	GridFinderComponent,
	GridFinderDataSourceDirective,
	GridFinderInMemoryDirective,
	GridOptionBarComponent,
	GridOptionBarTemplateDirective,
	GridPaginatorComponent,
	GridSelectBarComponent,
	GridSelectBarTemplateDirective,
	GridSharedBeans,
} from './components';

import {
	GridContextMenuDirective,
	GridDataDirective,
	GridDragSourceDirective,
	GridExtFeatureDirective,
	GridInfiniteScrollDirective,
	GridModelDataDirective,
	GridMultipleSelectorComponent,
	GridNoRowsOverlayDirective,
	GridNoRowsOverlayTemplateComponent,
	GridOptionsWrapperExt,
	GridPaginationDirective,
	GridPrecalculatedTreeDataDirective,
	GridSelectableDirective,
	GridSelectAllHeaderComponent,
	GridSingleSelectorComponent,
	GridTemplateBasedOptionBarComponent,
	GridTemplateBasedSelectBarComponent,
	GridTreeDataDirective,
	GridViewportRowModel,
	GridViewportStateDirective,
} from './features';

import { GRID_SETTINGS_DIALOG, GridDefinitionFactory } from './layout';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';

Grid['RowModelClasses'].viewport = GridViewportRowModel;
Grid.setFrameworkBeans([GridOptionsWrapperExt, GridSharedBeans]);

export interface CoreUiGridModuleConfig {
  gridSettingsDialogType: Type<any>;
}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    OverlayModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatRadioModule,
    MatDatepickerModule,
    MatIconModule,
    MatSelectModule,
    MatListModule,
    MatTooltipModule,
    MatButtonModule,
    MatMenuModule,

    CoreMlsModule.forChild(),
    CoreTypeModule.forChild(),
    CoreUiCommonModule.forChild(),
    CoreUiFormModule.forChild(),
    CoreUiDialogsModule.forChild(),
  ],
  declarations: [
    GridComponent,
    GridColumnComponent,
    GridCellTemplateDirective,
    GridOptionBarComponent,
    GridOptionBarTemplateDirective,
    GridSelectBarComponent,
    GridSelectBarTemplateDirective,
    GridSelectableDirective,
    GridContextMenuDirective,
    GridExtFeatureDirective,
    GridDataDirective,
    GridDragSourceDirective,
    GridTreeDataDirective,
    GridPrecalculatedTreeDataDirective,
    GridNoRowsOverlayDirective,

    GridTemplateBasedOptionBarComponent,
    GridTemplateBasedSelectBarComponent,
    GridPaginationDirective,
    GridInfiniteScrollDirective,
    GridViewportStateDirective,
    GridModelDataDirective,
    GridFinderComponent,
    GridFinderInMemoryDirective,
    GridFinderDataSourceDirective,

    GridEditableCellRendererComponent,
    GridCheckboxCellRendererComponent,
    GridRadioCellRendererComponent,
    GridSelectCellEditorComponent,
    GridTextCellEditorComponent,
    GridDateTimeCellEditorComponent,
    GridTextareaCellEditorComponent,
    GridNumberCellEditorComponent,
    GridQuantityCellEditorComponent,
    GridMaskedCellEditorComponent,
    GridAutocompleteCellEditorComponent,
    GridCellEditorContainerComponent,
    GridCellTooltipDirective,
    GridTemplateCellRendererComponent,
    GridButtonCellRendererComponent,
    GridTooltipCellRendererComponent,

    GridMultipleSelectorComponent,
    GridSingleSelectorComponent,
    GridSelectAllHeaderComponent,
    GridNoRowsOverlayTemplateComponent,

    GridPaginatorComponent,
  ],
  exports: [
    GridComponent,
    GridColumnComponent,
    GridCellTemplateDirective,
    GridOptionBarComponent,
    GridOptionBarTemplateDirective,
    GridSelectBarComponent,
    GridSelectBarTemplateDirective,
    GridPaginationDirective,
    GridInfiniteScrollDirective,
    GridViewportStateDirective,
    GridSelectableDirective,
    GridContextMenuDirective,
    GridExtFeatureDirective,
    GridFinderComponent,
    GridFinderInMemoryDirective,
    GridFinderDataSourceDirective,
    GridModelDataDirective,
    GridDataDirective,
    GridDragSourceDirective,
    GridTreeDataDirective,
    GridPrecalculatedTreeDataDirective,
    GridNoRowsOverlayDirective,
    GridCellEditorContainerComponent,

    GridPaginatorComponent,
  ],
  entryComponents: [
    GridTemplateBasedOptionBarComponent,
    GridTemplateBasedSelectBarComponent,

    GridEditableCellRendererComponent,
    GridCheckboxCellRendererComponent,
    GridRadioCellRendererComponent,
    GridSelectCellEditorComponent,
    GridTextCellEditorComponent,
    GridDateTimeCellEditorComponent,
    GridTextareaCellEditorComponent,
    GridNumberCellEditorComponent,
    GridQuantityCellEditorComponent,
    GridMaskedCellEditorComponent,
    GridAutocompleteCellEditorComponent,
    GridTemplateCellRendererComponent,
    GridButtonCellRendererComponent,
    GridTooltipCellRendererComponent,

    GridMultipleSelectorComponent,
    GridSingleSelectorComponent,
    GridSelectAllHeaderComponent,
    GridNoRowsOverlayTemplateComponent,
  ],
})
export class CoreUIGridModule {
  static forRoot(options: CoreUiGridModuleConfig): ModuleWithProviders<any> {
    return {
      ngModule: CoreUIGridModule,
      providers: [
        GridDefinitionFactory,
        { provide: GRID_SETTINGS_DIALOG, useValue: options.gridSettingsDialogType },
      ],
    };
  }

  static forChild(): ModuleWithProviders<any> {
    return {
      ngModule: CoreUIGridModule,
      providers: [],
    };
  }
}
